import { type MouseEvent, type RefObject, useCallback, useEffect, useRef } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

export type UseWorkspaceAgentMentionRowInteractionParams = {
  index: number;
  isDisabled?: boolean;
  isSelected: boolean;
  /** A pick is by id: a same-query refetch between paint and click can move the row off this index. */
  itemId: string;
  onHighlight: (index: number) => void;
  /** Image rows only: the hover preview anchors to the highlighted row without `getElementById`. */
  onPreviewAnchorElementChange?: (element: HTMLElement | null) => void;
  onSelect: (itemId: string) => void;
};

export type UseWorkspaceAgentMentionRowInteractionResult<TElement extends HTMLElement> = {
  handleClick: () => void;
  handleMouseDown: (event: MouseEvent<HTMLElement>) => void;
  handleMouseEnter: () => void;
  rowRef: RefObject<TElement>;
};

/** Pointer and scroll behaviour every picker row shares; the composer still owns the keyboard. */
export const useWorkspaceAgentMentionRowInteraction = <TElement extends HTMLElement>({
  index,
  isDisabled = false,
  isSelected,
  itemId,
  onHighlight,
  onPreviewAnchorElementChange,
  onSelect,
}: UseWorkspaceAgentMentionRowInteractionParams): UseWorkspaceAgentMentionRowInteractionResult<TElement> => {
  const rowRef = useRef<TElement>(null);
  const isPointerHighlightRef = useRef<boolean>(false);

  useIsomorphicLayoutEffect(() => {
    if (!onPreviewAnchorElementChange || !isSelected) {
      return;
    }

    onPreviewAnchorElementChange(rowRef.current);

    return () => {
      onPreviewAnchorElementChange(null);
    };
  }, [isSelected, onPreviewAnchorElementChange]);

  useEffect(() => {
    // Only the keyboard scrolls the list: hovering a half-visible row must not shift it under the pointer.
    if (isSelected && !isPointerHighlightRef.current) {
      rowRef.current?.scrollIntoView({ block: 'nearest' });
    }

    isPointerHighlightRef.current = false;
  }, [isSelected]);

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    onSelect(itemId);
  }, [isDisabled, itemId, onSelect]);

  const handleMouseEnter = useCallback(() => {
    isPointerHighlightRef.current = true;
    onHighlight(index);
  }, [index, onHighlight]);

  /** Pointer selection must not blur the editor, or the pill insert loses its range. */
  const handleMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  return { handleClick, handleMouseDown, handleMouseEnter, rowRef };
};
